import { useIsLoggedInQuery, useUserQuery } from '@repo/cf-user/hooks/auth'
import { useStudioLangCookie } from 'cf-i18n/cookies'
import { useStudioRawPathname } from '@repo/studio-config/studio-config-router'

import { resolveStudioLanguageForUrl } from './resolve-studio-language-for-url'
import type { StudioLanguageForUrlQueryResult } from './resolve-studio-language-for-url'

/**
 * Resolves the locale used by Studio links and URL synchronization. Stored
 * preferences (cookie when logged out, account when logged in) win over an
 * explicit URL prefix once set; URL locales only seed language on first visit.
 */
export function useStudioLanguageForUrlQuery(): StudioLanguageForUrlQueryResult {
  const pathname = useStudioRawPathname()
  const userQuery = useUserQuery(undefined, { select: data => data?.me?.user?.language })
  const isLoggedInQuery = useIsLoggedInQuery()
  const [cookieLang] = useStudioLangCookie()

  return resolveStudioLanguageForUrl({
    pathname,
    accountLanguage: userQuery.data,
    cookieLanguage: cookieLang,
    isLoggedIn: isLoggedInQuery.data,
    isAuthResolved: isLoggedInQuery.isSuccess,
    isAccountLanguageSettled: !userQuery.isPending,
  })
}

/**
 * Gate for `useSyncLocaleUrl`: wait for the account language fetch to settle
 * when logged in, but still sync on error using cookie/default fallbacks.
 */
export function useStudioLanguageForUrlSyncReady(): boolean {
  const userQuery = useUserQuery()
  const { data: isLoggedIn } = useIsLoggedInQuery()

  // While auth is pending, `isLoggedIn` is undefined so `!isLoggedIn` is true and we
  // sync cookie/default immediately. Intentional — cookie and account language are
  // almost always aligned, so waiting for auth would only delay the common case.
  return !isLoggedIn || !userQuery.isPending
}
